import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useAppSelector } from "../../../redux/hooks";
import { useCreateProgram } from "../../../modules/programs/api/useCreateProgram";
import { type ProgramFormValues } from "../../../modules/programs/programFormValues";
import { errorToast } from "../../../utils/toasts";
import { type Program } from "../../../zodios/apiDefinitions/api/programs";
import InlineProgramForm from "./InlineProgramForm";
import { ProgramForm } from "./ProgramForm";
import SelectTemplate from "./SelectTemplate";

interface Props {
  onSuccess: (program: Program) => void;
  /** Omit to hide the Cancel button. */
  onCancel?: () => void;
  pricingPlanId?: number;
  overrideSize?: "xs" | null;
  onFormChange?: () => void;
  /** Hands the submitted values to `onSubmit` instead of creating the program. */
  deferProgramCreation?: boolean;
  onSubmit?: (values: ProgramFormValues, templateType: string) => void;
  /** Bidding skips the template picker. */
  engine?: "bidding";
}

export const CreateProgram = ({
  onSuccess,
  onCancel,
  pricingPlanId,
  overrideSize = null,
  onFormChange,
  deferProgramCreation = false,
  onSubmit,
  engine,
}: Props) => {
  const { t } = useTranslation(["programs"]);
  const isStaff = useAppSelector((state) => state.user.is_staff);

  const [programTemplate, setProgramTemplate] = useState<string | null>(engine === "bidding" ? "CUSTOM" : null);

  const { handleSubmit, ...form } = useForm<ProgramFormValues>({
    shouldUnregister: false,
    mode: "onChange",
  });

  if (form.formState.isDirty) {
    onFormChange?.();
  }

  const createProgram = useCreateProgram({ pricingPlanId, onSuccess });

  return (
    <FormProvider {...form} handleSubmit={handleSubmit}>
      {programTemplate === null ?
        <SelectTemplate isStaff={isStaff} onSelect={setProgramTemplate} />
      : <ProgramForm
          submitLabel={t(($) => $.programs.footer.Create)}
          onCancel={onCancel}
          onSubmit={handleSubmit(
            (values) => {
              if (deferProgramCreation) {
                // The caller creates it later, so it needs the template to split the fields.
                onSubmit?.(values, programTemplate);
              } else {
                createProgram.mutate({ values, templateType: programTemplate });
              }
            },
            () => errorToast({ message: t(($) => $.programs["Some fields are missing or invalid."]) }),
          )}
          isPending={createProgram.isPending || form.formState.isSubmitting}
        >
          <InlineProgramForm form={form} programTemplate={programTemplate} size={overrideSize ?? undefined} />
        </ProgramForm>
      }
    </FormProvider>
  );
};
